// --- Dependencies -------------------------------------------------------------------------------
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import type { TyCouponPreview, TySubscriptionPeriod } from "@/types";
import CouponBadge from "@/UI/components/CouponBadge";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type ConfirmSubscriptionPopupDetails = {
    period: TySubscriptionPeriod;
    branches: number;
    planPrice: number;

    /** Price of one extra branch for the whole selected period, not per month. */
    addonPrice: number;

    totalPrice: number;
    coupon: TyCouponPreview | null;
    onConfirm: () => void;
};

type Props = {
    onClose: () => void;
    details?: ConfirmSubscriptionPopupDetails;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * Subscribing is a one-click POST that puts the store into a pending, pay-by-transfer state, so a
 * misclick is expensive to undo. This lays out exactly what is being bought and how the money moves
 * before anything is created.
 */
export default function ConfirmSubscriptionPopup({ onClose, details }: Props) {
    const t = useTranslation();
    if (!details) return null;

    const { period, branches, planPrice, addonPrice, totalPrice, coupon, onConfirm } = details;

    const periodLabels: Record<TySubscriptionPeriod, string> = {
        monthly: t.subscriptions.periodMonthly,
        quarterly: t.subscriptions.periodQuarterly,
        annually: t.subscriptions.periodAnnually,
    };

    const extraBranches = Math.max(0, branches - 1);

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    return (
        <div className="csp">
            <div className="csp-header">
                <p className="csp-title">{t.subscriptions.confirmTitle}</p>
                <p className="csp-subtitle">{t.subscriptions.confirmSubtitle}</p>
            </div>

            <div className="csp-summary">
                <div className="csp-row">
                    <span className="csp-row-label">{t.subscriptions.periodLabel}</span>
                    <span className="csp-row-value">{periodLabels[period]}</span>
                </div>
                <div className="csp-row">
                    <span className="csp-row-label">{t.subscriptions.planPrice}</span>
                    <span className="csp-row-value">
                        {planPrice.toLocaleString()} {t.subscriptions.egp}
                    </span>
                </div>
                <div className="csp-row">
                    <span className="csp-row-label">{t.subscriptions.branchesLabel}</span>
                    <span className="csp-row-value">
                        {branches} {t.subscriptions.branchCount}
                    </span>
                </div>
                {extraBranches > 0 && (
                    <div className="csp-row">
                        <span className="csp-row-label">{t.subscriptions.confirmExtraBranchesLabel}</span>
                        <span className="csp-row-value">
                            {extraBranches} × {addonPrice.toLocaleString()} = {(extraBranches * addonPrice).toLocaleString()} {t.subscriptions.egp}
                        </span>
                    </div>
                )}
            </div>

            {coupon && (
                <div className="csp-coupon">
                    <CouponBadge code={coupon.code} discount={coupon.discount} savedAmount={coupon.discountAmount} compact />
                </div>
            )}

            <div className="csp-total">
                <span className="csp-total-label">{t.subscriptions.totalLabel}</span>
                <span className="csp-total-value">
                    {totalPrice.toLocaleString()} <span className="csp-total-egp">{t.subscriptions.egp}</span>
                </span>
            </div>

            <div className="csp-payment">
                <p className="csp-payment-title">{t.subscriptions.confirmPaymentTitle}</p>
                <p className="csp-payment-note">{t.subscriptions.confirmPaymentNote}</p>
                <p className="csp-payment-warn">{t.subscriptions.payWindowTitle}</p>
            </div>

            <div className="csp-actions">
                <button type="button" className="csp-cancel" onClick={onClose}>
                    {t.subscriptions.confirmCancelButton}
                </button>
                <button type="button" className="csp-confirm" onClick={handleConfirm}>
                    {t.subscriptions.confirmSubmitButton}
                </button>
            </div>
        </div>
    );
}
